/**
 * Aba Anotações & campos customizados do funcionário (E10b — integração
 * Convenia §3.9): somente leitura — os dados chegam pelo sync (domínios
 * "anotacoes" e "campos_customizados"). As anotações são texto livre do RH
 * (insumo humano, nunca parsing automático); os campos customizados incluem as
 * adesões de benefício (VT/seguro/plano/home office) e equipamentos.
 */
import { useEffect, useState } from "react";
import { Badge, Card, Group, Table, Text, Title } from "@mantine/core";
import { api } from "../../api";
import { fmtData } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th } from "../../componentes/listas";

interface Anotacao {
  id: string;
  conveniaId: number | null;
  titulo: string;
  data: string | null;
  texto: string | null;
}
interface Campo {
  definicaoUuid: string;
  valor: string | null;
  nome: string;
  tipo: string | null;
  secao: string | null;
}

export function AnotacoesCamposTab({ funcionarioId }: { funcionarioId: string }) {
  const [anotacoes, setAnotacoes] = useState<Anotacao[] | null>(null);
  const [campos, setCampos] = useState<Campo[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ anotacoes: Anotacao[] }>(`/funcionarios/${funcionarioId}/anotacoes`),
      api.get<{ campos: Campo[] }>(`/funcionarios/${funcionarioId}/campos-customizados`),
    ])
      .then(([a, c]) => {
        setAnotacoes(a.anotacoes);
        setCampos(c.campos);
      })
      .catch(setErro);
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!anotacoes || !campos) return <Carregando linhas={4} />;

  return (
    <>
      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Anotações do RH <Badge variant="light">{anotacoes.length}</Badge>
        </Title>
        {anotacoes.length === 0 ? (
          <Vazio texto="Nenhuma anotação sincronizada — rode o sync Convenia (domínio anotacoes) para esta pessoa." />
        ) : (
          <>
            {anotacoes.map((a) => (
              <Card key={a.id} withBorder mb="sm" bg="gray.0">
                <Group justify="space-between" mb={4}>
                  <Text size="sm" fw={700}>
                    {a.titulo}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {a.data ? fmtData(a.data) : "sem data"}
                  </Text>
                </Group>
                {a.texto && (
                  <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                    {a.texto}
                  </Text>
                )}
              </Card>
            ))}
          </>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Campos customizados do Convenia <Badge variant="light">{campos.length}</Badge>
        </Title>
        {campos.length === 0 ? (
          <Vazio texto="Nenhum valor sincronizado — rode o sync Convenia (domínio campos_customizados) para esta pessoa." />
        ) : (
          <TabelaDados maw={860}>
            <Table.Thead>
              <Table.Tr>
                <Th>Seção</Th>
                <Th>Campo</Th>
                <Th>Valor</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {campos.map((c) => (
                <Table.Tr key={c.definicaoUuid}>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{c.secao ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.nome}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {c.valor ?? "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
      <Group>
        <Text size="xs" c="dimmed">
          Dados somente leitura — a fonte é o Convenia. As adesões de benefício (VT/seguro/plano) viram propostas de
          vínculo no E10c; aqui são espelho informativo.
        </Text>
      </Group>
    </>
  );
}
